import { useState, useEffect, useRef } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, CheckCircle, Archive, Eye, Upload,
  Image as ImageIcon, Calendar, ToggleLeft, ToggleRight, Clock, RotateCcw
} from 'lucide-react';

const EMPTY_FORM = {
  name: '', hero_ueberschrift: '', hero_unterzeile: '', hero_button: '',
  hero_hinweistext: '', hero_teaserpreis: '', hero_preishinweis: '',
  hero_uebernahmehinweis: '', kooperation_hinweis: '', image_url: '',
  start_date: '', end_date: '',
};

const TEXT_FIELDS = [
  { key: 'name', label: 'Interner Name / Titel', required: true },
  { key: 'hero_ueberschrift', label: 'Ueberschrift' },
  { key: 'hero_unterzeile', label: 'Unterzeile' },
  { key: 'hero_button', label: 'Button-Text' },
  { key: 'hero_hinweistext', label: 'Hinweistext', multiline: true },
  { key: 'hero_teaserpreis', label: 'Teaserpreis (optional)' },
  { key: 'hero_preishinweis', label: 'Preishinweis (optional)' },
  { key: 'hero_uebernahmehinweis', label: 'Uebernahmehinweis (optional)' },
  { key: 'kooperation_hinweis', label: 'Kooperationshinweis (optional)', multiline: true },
];

const STATUS_CONFIG = {
  active: { label: 'Aktiv (Standard)', color: 'bg-green-100 text-green-700' },
  bereit: { label: 'Bereit', color: 'bg-blue-100 text-blue-700' },
  archived: { label: 'Archiviert', color: 'bg-slate-100 text-slate-500' },
};

function ModeToggle({ mode, onToggle, loading }) {
  const isAuto = mode === 'automatisch';
  return (
    <Card className="border-0 shadow-sm rounded-sm" data-testid="hero-mode-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isAuto ? <Clock className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} /> : <ToggleLeft className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />}
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-900">Hero-Modus</h3>
              <p className="text-xs text-slate-500 font-body mt-0.5">
                {isAuto
                  ? 'Automatisch: Hero wechselt nach Zeitraum (Start-/Enddatum)'
                  : 'Manuell: Sie waehlen selbst, welcher Hero aktiv ist'}
              </p>
            </div>
          </div>
          <button
            onClick={onToggle} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-sm border border-slate-200 hover:bg-slate-50 transition-colors font-body text-sm"
            data-testid="hero-mode-toggle"
          >
            {isAuto ? (
              <><ToggleRight className="w-5 h-5 text-[#0E518D]" /> <span className="font-medium text-[#0E518D]">Automatisch</span></>
            ) : (
              <><ToggleLeft className="w-5 h-5 text-slate-500" /> <span className="font-medium text-slate-700">Manuell</span></>
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function ComboFormDialog({ open, onClose, combo, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!combo;

  useEffect(() => {
    if (combo) {
      setForm({
        name: combo.name || '', hero_ueberschrift: combo.hero_ueberschrift || '',
        hero_unterzeile: combo.hero_unterzeile || '', hero_button: combo.hero_button || '',
        hero_hinweistext: combo.hero_hinweistext || '', hero_teaserpreis: combo.hero_teaserpreis || '',
        hero_preishinweis: combo.hero_preishinweis || '',
        hero_uebernahmehinweis: combo.hero_uebernahmehinweis || '',
        kooperation_hinweis: combo.kooperation_hinweis || '', image_url: combo.image_url || '',
        start_date: combo.start_date || '', end_date: combo.end_date || '',
      });
    } else { setForm(EMPTY_FORM); }
  }, [combo, open]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Bitte einen Namen eingeben'); return; }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      toast.error('Startdatum muss vor dem Enddatum liegen'); return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await adminAPI.updateHeroCombination(combo.id, form);
        toast.success('Hero-Kombination aktualisiert');
      } else {
        await adminAPI.createHeroCombination(form);
        toast.success('Hero-Kombination erstellt');
      }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await adminAPI.uploadHeroImage(combo.id, fd);
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success('Bild hochgeladen'); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler beim Hochladen'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Hero-Kombination bearbeiten' : 'Neue Hero-Kombination'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {TEXT_FIELDS.map(f => (
            <div key={f.key}>
              <Label className="font-body text-sm">{f.label}</Label>
              {f.multiline ? (
                <Textarea value={form[f.key]} rows={2} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  className="mt-1 rounded-sm font-body" data-testid={`hero-form-${f.key}`} />
              ) : (
                <Input value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  className="mt-1 rounded-sm font-body" data-testid={`hero-form-${f.key}`} />
              )}
            </div>
          ))}

          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm border border-slate-200">
            <div>
              <Label className="font-body text-sm flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#0E518D]" /> Startdatum (optional)
              </Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                className="mt-1 rounded-sm font-body" data-testid="hero-form-start-date" />
            </div>
            <div>
              <Label className="font-body text-sm flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-[#0E518D]" /> Enddatum (optional)
              </Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                className="mt-1 rounded-sm font-body" data-testid="hero-form-end-date" />
            </div>
            <p className="col-span-2 text-xs text-slate-400 font-body">
              Zeitraum wird nur im automatischen Modus beruecksichtigt. Neue Kombinationen sind sofort als "Bereit" verfuegbar.
            </p>
          </div>

          <div>
            <Label className="font-body text-sm">Bild-URL</Label>
            <div className="flex gap-2 mt-1">
              <Input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))}
                placeholder="URL oder ueber Upload setzen" className="rounded-sm font-body flex-1" data-testid="hero-form-image-url" />
              {isEdit && (
                <>
                  <input type="file" ref={fileRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                  <Button type="button" variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()}
                    disabled={uploading} data-testid="hero-form-upload-btn">
                    {uploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0E518D]" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </>
              )}
            </div>
            {!isEdit && <p className="text-xs text-slate-400 font-body mt-1">Bild-Upload ist nach dem Erstellen moeglich.</p>}
            <p className="text-xs text-slate-400 font-body mt-1">Empfohlen: 1920 x 1080 px (16:9)</p>
          </div>

          {form.image_url && (
            <div className="rounded-sm overflow-hidden border border-slate-200">
              <img src={form.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url}
                alt="Vorschau" className="w-full h-40 object-cover" />
            </div>
          )}

          <Button onClick={handleSave} disabled={saving}
            className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body" data-testid="hero-form-save">
            {saving ? 'Speichern...' : (isEdit ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ open, onClose, combo }) {
  if (!combo) return null;
  const imgSrc = combo.image_url
    ? (combo.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${combo.image_url}` : combo.image_url) : null;
  const cfg = STATUS_CONFIG[combo.status] || STATUS_CONFIG.bereit;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <div className="bg-white">
          <div className="flex items-center justify-between px-6 pt-5 pb-3">
            <h3 className="font-heading text-lg font-semibold">Vorschau: {combo.name}</h3>
            <div className="flex gap-2">
              {combo.start_date && combo.end_date && (
                <Badge className="bg-blue-50 text-blue-700 text-xs">
                  {new Date(combo.start_date).toLocaleDateString('de-DE')} - {new Date(combo.end_date).toLocaleDateString('de-DE')}
                </Badge>
              )}
              <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 pt-0">
            <div className="space-y-4">
              {combo.kooperation_hinweis && (
                <div className="inline-flex items-center gap-2 bg-[#0E518D]/8 text-[#0E518D] px-3 py-1.5 rounded-full text-xs font-body">{combo.kooperation_hinweis}</div>
              )}
              <h1 className="font-heading text-3xl font-bold text-slate-900 leading-tight">{combo.hero_ueberschrift || 'Ueberschrift'}</h1>
              <p className="text-base text-slate-600 font-body">{combo.hero_unterzeile}</p>
              <div className="flex items-center gap-3">
                <div className="bg-[#0E518D] text-white px-5 py-2 rounded-sm text-sm font-body font-medium">{combo.hero_button || 'Button'}</div>
                {combo.hero_teaserpreis && (
                  <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1.5 rounded-sm">
                    <span className="text-[#0E518D] font-body font-bold text-sm">{combo.hero_teaserpreis}</span>
                    {combo.hero_preishinweis && <span className="text-slate-500 text-xs font-body ml-2">{combo.hero_preishinweis}</span>}
                  </div>
                )}
              </div>
              {combo.hero_uebernahmehinweis && <p className="text-slate-500 text-sm font-body">{combo.hero_uebernahmehinweis}</p>}
            </div>
            <div>
              {imgSrc ? (
                <img src={imgSrc} alt="Hero" className="w-full h-56 object-cover rounded-sm shadow-lg" />
              ) : (
                <div className="w-full h-56 bg-slate-100 rounded-sm flex items-center justify-center"><ImageIcon className="w-12 h-12 text-slate-300" /></div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function isDateActive(combo) {
  if (!combo.start_date || !combo.end_date) return false;
  const today = new Date().toISOString().slice(0, 10);
  return combo.start_date <= today && today <= combo.end_date;
}

function ComboCard({ combo, onEdit, onPreview, onActivate, onArchive, onReactivate, onDelete, heroMode }) {
  const isActive = combo.status === 'active';
  const isBereit = combo.status === 'bereit';
  const isArchived = combo.status === 'archived';
  const isAutoActive = heroMode === 'automatisch' && !isArchived && isDateActive(combo);
  const cfg = STATUS_CONFIG[combo.status] || STATUS_CONFIG.bereit;
  const imgSrc = combo.image_url
    ? (combo.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${combo.image_url}` : combo.image_url) : null;

  return (
    <Card className={`border-0 shadow-sm rounded-sm mb-3 ${isActive ? 'ring-2 ring-green-300/50' : ''} ${isAutoActive && !isActive ? 'ring-2 ring-blue-300/50' : ''}`}
      data-testid={`hero-combo-${combo.id}`}>
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-48 h-32 sm:h-auto flex-shrink-0">
            {imgSrc ? (
              <img src={imgSrc} alt={combo.name} className="w-full h-full object-cover rounded-l-sm" />
            ) : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center rounded-l-sm min-h-[120px]">
                <ImageIcon className="w-8 h-8 text-slate-300" />
              </div>
            )}
          </div>
          <div className="flex-1 p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-heading text-base font-semibold text-slate-900">{combo.name}</h3>
              <Badge className={`${cfg.color} text-xs`}>{cfg.label}</Badge>
              {isAutoActive && <Badge className="bg-blue-50 text-blue-700 text-xs">Wird aktuell ausgespielt</Badge>}
            </div>
            <p className="text-sm text-slate-600 font-body line-clamp-1">{combo.hero_ueberschrift}</p>
            {combo.hero_unterzeile && <p className="text-xs text-slate-400 font-body line-clamp-1 mt-0.5">{combo.hero_unterzeile}</p>}

            {(combo.start_date || combo.end_date) && (
              <div className="flex items-center gap-1.5 mt-2">
                <Calendar className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />
                <span className="text-xs text-slate-500 font-body">
                  {combo.start_date ? new Date(combo.start_date).toLocaleDateString('de-DE') : '...'} - {combo.end_date ? new Date(combo.end_date).toLocaleDateString('de-DE') : '...'}
                </span>
                {isAutoActive && <span className="text-xs text-blue-600 font-body font-medium ml-1">(heute gueltig)</span>}
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 mt-3">
              <Button variant="ghost" size="sm" onClick={onPreview} className="text-slate-600 hover:text-[#0E518D] h-8 text-xs font-body" data-testid={`hero-preview-${combo.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Vorschau
              </Button>
              <Button variant="ghost" size="sm" onClick={onEdit} className="text-slate-600 hover:text-[#0E518D] h-8 text-xs font-body" data-testid={`hero-edit-${combo.id}`}>
                <Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten
              </Button>
              {!isActive && !isArchived && onActivate && (
                <Button variant="ghost" size="sm" onClick={onActivate} className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 text-xs font-body" data-testid={`hero-activate-${combo.id}`}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Als Standard
                </Button>
              )}
              {!isActive && !isArchived && onArchive && (
                <Button variant="ghost" size="sm" onClick={onArchive} className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 text-xs font-body" data-testid={`hero-archive-${combo.id}`}>
                  <Archive className="w-3.5 h-3.5 mr-1" /> Archivieren
                </Button>
              )}
              {isArchived && onReactivate && (
                <Button variant="ghost" size="sm" onClick={onReactivate} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 text-xs font-body" data-testid={`hero-reactivate-${combo.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reaktivieren
                </Button>
              )}
              {!isActive && (
                <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 text-xs font-body" data-testid={`hero-delete-${combo.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Loeschen
                </Button>
              )}
            </div>
            <p className="text-[10px] text-slate-300 font-body mt-2">
              Erstellt: {new Date(combo.created_at).toLocaleDateString('de-DE')}
              {combo.updated_at !== combo.created_at && ` | Geaendert: ${new Date(combo.updated_at).toLocaleDateString('de-DE')}`}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HeroCombinations() {
  const [combos, setCombos] = useState([]);
  const [heroMode, setHeroMode] = useState('manuell');
  const [loading, setLoading] = useState(true);
  const [modeLoading, setModeLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editCombo, setEditCombo] = useState(null);
  const [previewCombo, setPreviewCombo] = useState(null);

  const load = async () => {
    try {
      const [combosRes, modeRes] = await Promise.all([adminAPI.getHeroCombinations(), adminAPI.getHeroMode()]);
      setCombos(combosRes.data);
      setHeroMode(modeRes.data.hero_mode || 'manuell');
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleMode = async () => {
    const newMode = heroMode === 'manuell' ? 'automatisch' : 'manuell';
    setModeLoading(true);
    try {
      await adminAPI.setHeroMode(newMode);
      setHeroMode(newMode);
      toast.success(`Hero-Modus auf "${newMode}" gesetzt`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setModeLoading(false); }
  };

  const handleActivate = async (id) => {
    try { await adminAPI.activateHeroCombination(id); toast.success('Als Standard aktiviert'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };
  const handleArchive = async (id) => {
    try { await adminAPI.archiveHeroCombination(id); toast.success('Archiviert'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };
  const handleReactivate = async (id) => {
    try { await adminAPI.reactivateHeroCombination(id); toast.success('Reaktiviert (Status: Bereit)'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Diese Hero-Kombination wirklich loeschen? Dieser Vorgang kann nicht rueckgaengig gemacht werden.')) return;
    try { await adminAPI.deleteHeroCombination(id); toast.success('Geloescht'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E518D]" /></div>;
  }

  const activeList = combos.filter(c => c.status === 'active');
  const bereitList = combos.filter(c => c.status === 'bereit');
  const archivedList = combos.filter(c => c.status === 'archived');

  const cardProps = (combo) => ({
    combo, heroMode,
    onEdit: () => { setEditCombo(combo); setShowForm(true); },
    onPreview: () => setPreviewCombo(combo),
    onActivate: () => handleActivate(combo.id),
    onArchive: () => handleArchive(combo.id),
    onReactivate: () => handleReactivate(combo.id),
    onDelete: () => handleDelete(combo.id),
  });

  return (
    <div className="space-y-6" data-testid="hero-combinations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Hero-Kombinationen</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Hero-Bereiche verwalten, aktivieren und zeitgesteuert ausspielen</p>
        </div>
        <Button onClick={() => { setEditCombo(null); setShowForm(true); }}
          className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm font-body" data-testid="hero-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Neue Kombination
        </Button>
      </div>

      <ModeToggle mode={heroMode} onToggle={toggleMode} loading={modeLoading} />

      {heroMode === 'automatisch' && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 text-sm text-blue-800 font-body" data-testid="auto-mode-info">
          <strong>Automatischer Modus aktiv:</strong> Das System waehlt die Hero-Kombination mit passendem Zeitraum. Nur Kombinationen im Status "Bereit" mit gueltigem Datum werden beruecksichtigt. Fallback: Standard-Kombination (gruen).
        </div>
      )}

      {activeList.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Standard (Fallback)</h2>
          {activeList.map(c => <ComboCard key={c.id} {...cardProps(c)} />)}
        </div>
      )}

      {bereitList.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-blue-800 mb-3">Bereit / Geplant ({bereitList.length})</h2>
          <div className="space-y-3">{bereitList.map(c => <ComboCard key={c.id} {...cardProps(c)} />)}</div>
        </div>
      )}

      {archivedList.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-500 mb-3">Archiviert ({archivedList.length})</h2>
          <div className="space-y-3">{archivedList.map(c => <ComboCard key={c.id} {...cardProps(c)} />)}</div>
        </div>
      )}

      {combos.length === 0 && (
        <Card className="border-0 shadow-sm rounded-sm"><CardContent className="p-10 text-center">
          <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 font-body">Noch keine Hero-Kombinationen vorhanden.</p>
        </CardContent></Card>
      )}

      <ComboFormDialog open={showForm} onClose={() => setShowForm(false)} combo={editCombo} onSaved={load} />
      <PreviewDialog open={!!previewCombo} onClose={() => setPreviewCombo(null)} combo={previewCombo} />
    </div>
  );
}
